import {
  Badge,
  Group,
  NumberInput,
  Paper,
  Text,
  Tooltip,
  type NumberInputProps,
} from '@mantine/core';
import { formatMoney } from 'src/common/currency';
import { MetricHint, type MetricHintKey } from 'src/secret-menu/billing/MetricHint';

export const formatUnits = (value: number) => value.toLocaleString('en-GB');

/** Revenue in pounds, with pence only when there are some: "£55,000", not "£55,000.00". */
export const formatRevenue = (pence: number | null) => formatMoney((pence ?? 0) / 100, 'GBP');

function signOf(value: number): string {
  if (value > 0) return '+';
  if (value < 0) return '−';
  return '';
}

export const formatSignedPence = (pence: number) =>
  `${signOf(pence)}${formatRevenue(Math.abs(pence))}`;

export const formatSignedUnits = (units: number) =>
  `${signOf(units)}${formatUnits(Math.abs(units))}`;

/** A figure that includes Monument's terms, which are typed in rather than read from our data. */
export function ManualBadge() {
  return (
    <Tooltip
      label="Entered by hand from the Strategic partner button, not read from Afternoon's database"
      withArrow
    >
      <Badge size="xs" variant="light" color="orange">
        Manual
      </Badge>
    </Tooltip>
  );
}

type Tone = 'up' | 'down' | 'neutral';

export const toneOf = (change: number | null): Tone => {
  if (change === null || change === 0) return 'neutral';
  return change > 0 ? 'up' : 'down';
};

const TONE_COLORS: Record<Tone, string> = { up: 'green', down: 'red', neutral: 'dimmed' };

/** One headline number: what it is, the figure, and one line saying how it is moving. */
export function Stat({
  label,
  value,
  detail,
  tone = 'neutral',
  hint,
  badge,
}: {
  label: string;
  value: string;
  detail: string;
  tone?: Tone;
  hint: MetricHintKey;
  badge?: React.ReactNode;
}) {
  return (
    <Paper p="md" withBorder radius="md">
      <Group gap={6} wrap="nowrap" mb={4}>
        <Text size="textSm" c="dimmed">
          {label}
        </Text>
        <MetricHint hint={hint} />
        {badge}
      </Group>
      <Text fz={26} fw={700} lh={1.2} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Text>
      <Text size="textSm" c={TONE_COLORS[tone]} mt={2}>
        {detail}
      </Text>
    </Paper>
  );
}

/** A pound amount held in pence and edited in pounds, for fees and prices. */
export function MoneyInput({
  pence,
  onChange,
  ...props
}: Omit<NumberInputProps, 'value' | 'onChange'> & {
  pence: number;
  onChange: (pence: number) => void;
}) {
  return (
    <NumberInput
      prefix="£"
      thousandSeparator=","
      min={0}
      decimalScale={2}
      {...props}
      value={pence / 100}
      onChange={value => onChange(Math.round(Number(value || 0) * 100))}
    />
  );
}
